import { Paper, Box, CssBaseline, Typography, Chip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Sparkles } from 'lucide-react';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Loader from 'components/Loader';
import { useChatTheme } from '../theme';
import { useSnackbar } from 'components/GlobalSnackbar';
import { safeParseApiError } from 'utils/parseApiError';
import { filterAvailableFilters } from 'utils/filterPermissions';
import { extractReuploadFileNames } from 'utils/reuploadError';
import ReuploadFilesDialog from 'components/ReuploadFilesDialog';
import type { ReuploadReplacement } from 'components/ReuploadFilesDialog';
import type { Message } from '../api/model';
import type { ChatListItem } from '../components/ChatInfoPanel';
import type { ContextFileSelection } from 'types/contextDocuments';
import { getChatMessagesById, sendMessageToChat, stopChatById } from '../api';
import {
  useChatFilters,
  useStreamingMessage,
  usePromptManagement,
  useContextUsage,
  useChatList,
  useMessageAttachmentsDraft,
  DEFAULT_CHAT_TITLE,
} from '../hooks';
import {
  ChatInfoPanel,
  ChatSwitcher,
  ClearChatDialog,
  InputBox,
  InputBoxHandle,
  MessageList,
  Sidebar,
} from '../components';
import { getPromptTemplateCases, getPromptTemplates } from '../../prompthub/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface RootState {
  reducer: {
    project: { id: string };
    role?: { id: number | null; permissions?: Array<{ name: string }> };
    prompts: Array<{ id: number; content: string }>;
    promptsLoading: boolean;
  };
}

export default function ChatPage() {
  const currProject = useSelector((state: RootState) => state.reducer.project.id);
  const roleId = useSelector((state: RootState) => state.reducer.role?.id ?? null);
  const userRole = useSelector((state: RootState) => state.reducer.role);
  const prompts = useSelector((state: RootState) => state.reducer.prompts);
  const promptsLoading = useSelector((state: RootState) => state.reducer.promptsLoading);
  const showSnackbar = useSnackbar();

  const [chat, setChat] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [reuploadDialogNames, setReuploadDialogNames] = useState<string[] | null>(null);
  const queryClient = useQueryClient();
  const [messageText, setMessageText] = useState(() => {
    try {
      return localStorage.getItem('chat_message_text') || '';
    } catch {
      return '';
    }
  });
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputBoxRef = useRef<InputBoxHandle>(null);
  const skipNextMessagesLoadRef = useRef<string | null>(null);
  const [mermaidActivated, setMermaidActivated] = useState(false);

  const [analysysMode, setAnalysysMode] = useState<boolean>(() => {
    try {
      const mode = localStorage.getItem('analysys_mode');
      if (mode === 'true') {
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  });

  const [expandedGeneralInfo, setExpandedGeneralInfo] = useState(() => {
    try {
      const saved = localStorage.getItem('chat_general_info_expanded');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('chat_general_info_expanded', JSON.stringify(expandedGeneralInfo));
    } catch (error) {
      console.warn('Failed to save general info expanded state:', error);
    }
  }, [expandedGeneralInfo]);

  useEffect(() => {
    try {
      if (messageText) localStorage.setItem('chat_message_text', messageText);
      else localStorage.removeItem('chat_message_text');
    } catch (error) {
      console.warn('Failed to save message text to localStorage:', error);
    }
  }, [messageText]);

  // ─── Multi-chat ──────────────────────────────────────────────────────────────

  const { chats, activeChatId, isLoadingChats, switchChat, createChat, deleteChat } =
    useChatList(currProject);

  const chatListItems = useMemo<ChatListItem[]>(
    () =>
      chats.map((c) => ({
        id: c.id,
        label:
          c.title ??
          new Date(c.created_at * 1000).toLocaleDateString('ru', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          }),
      })),
    [chats]
  );

  // Load messages when active chat changes
  useEffect(() => {
    if (!activeChatId) {
      setChat([]);
      setMessagesLoading(false);
      return;
    }
    if (skipNextMessagesLoadRef.current === activeChatId) {
      skipNextMessagesLoadRef.current = null;
      return;
    }
    setChat([]);
    setMessagesLoading(true);
    getChatMessagesById(activeChatId)
      .then((messages) => setChat(messages))
      .catch(() => undefined)
      .finally(() => setMessagesLoading(false));
  }, [activeChatId]);

  // ─── Filters / hooks ─────────────────────────────────────────────────────────

  const {
    sourceFilters,
    toggleFilter,
    toggleContextChat,
    updateContextMode,
    updateContextLimit,
    updateSpecification,
    toggleSpecificationSelect,
    updateContextFiles,
    updateUrlSourceIds,
    getContextMessagesLimit,
  } = useChatFilters();

  const {
    streamingMessage,
    displayedContent,
    currentTaskId,
    progressLabel,
    progressTokens,
    resetStreaming,
  } = useStreamingMessage(chat, setChat, setLoading);

  const { onSavePrompt, onDeletePrompt } = usePromptManagement(currProject, roleId, showSnackbar);

  const { usageData, reuploadFileNames } = useContextUsage(
    sourceFilters,
    messageText,
    expandedGeneralInfo,
    getContextMessagesLimit,
    activeChatId
  );

  const attachments = useMessageAttachmentsDraft(currProject, activeChatId);

  // ─── Prompt templates ────────────────────────────────────────────────────────

  const { data: casesData, isLoading: casesLoading } = useQuery({
    queryKey: ['getPromptTemplateCases', roleId],
    queryFn: () => getPromptTemplateCases(roleId != null ? { role_id: roleId } : undefined),
    enabled: roleId != null,
    gcTime: 60000,
  });

  const { data: favoriteTemplates = [], isLoading: favoritesLoading } = useQuery({
    queryKey: ['chatSidebarFavoritePromptTemplates'],
    queryFn: async () => {
      const res = await getPromptTemplates({
        limit: 300,
        sort_column: 'created_at',
        sort_direction: 'DESC',
      });
      return (res.items ?? []).filter((t) => t.is_favorite);
    },
    staleTime: 60_000,
  });

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleSendMessage = useCallback(
    async (human_message: string, obfuscation = false) => {
      if (roleId == null) return;

      let chatId = activeChatId;
      if (!chatId) {
        const chat = await createChat(DEFAULT_CHAT_TITLE);
        if (!chat) {
          showSnackbar({ message: 'Не удалось создать чат', severity: 'error' });
          return;
        }
        chatId = chat.id;
        skipNextMessagesLoadRef.current = chatId;
      }

      setLoading(true);
      resetStreaming();
      setMessageText('');
      try {
        localStorage.removeItem('chat_message_text');
      } catch (error) {
        console.warn('Failed to remove message text from localStorage:', error);
      }

      const filteredFilters = filterAvailableFilters(sourceFilters, userRole || {}) as Record<
        string,
        unknown
      >;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { show_specification_select, context_mode, ...filters } = filteredFilters;

      const readyAttachmentIds = attachments.getReadyIds();
      const readyAttachmentSelections = attachments.getReadySelections();

      const requestData = {
        human_message,
        obfuscation,
        diagram_enabled: mermaidActivated,
        analysys_mode: analysysMode,
        ...filters,
        use_source_common: expandedGeneralInfo ? filters.use_source_common : false,
        use_context_files: expandedGeneralInfo ? filters.use_context_files : [],
        context_messages_limit: getContextMessagesLimit(),
        ...(readyAttachmentSelections.length > 0
          ? { message_attachments: readyAttachmentSelections }
          : {}),
      };

      const userMessage: Message = {
        role: 'User',
        content: human_message,
        created_at: new Date().toISOString(),
        attached_document_ids: readyAttachmentIds.length > 0 ? readyAttachmentIds : undefined,
      };
      setChat((prev) => [...prev, userMessage]);
      attachments.clear();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        });
      });

      sendMessageToChat(chatId, requestData, roleId)
        .then((response) => {
          if (response?.task_id) {
            localStorage.setItem('chat_task_id', response.task_id);
            localStorage.setItem(
              `stream_chat_${response.task_id}`,
              JSON.stringify({ role: 'AI', content: '', isStreaming: true })
            );
          }
        })
        .catch((error: unknown) => {
          const reuploadNames = extractReuploadFileNames(error);
          if (reuploadNames) {
            setReuploadDialogNames(reuploadNames);
          } else {
            const { generalMessage } = safeParseApiError(error);
            showSnackbar({
              message: generalMessage || 'Ошибка отправки сообщения',
              severity: 'error',
            });
          }
          setLoading(false);
        });
    },
    [
      activeChatId,
      createChat,
      resetStreaming,
      sourceFilters,
      userRole,
      roleId,
      expandedGeneralInfo,
      getContextMessagesLimit,
      showSnackbar,
      attachments,
      mermaidActivated,
      analysysMode,
    ]
  );

  const handleChangeAnalysysMode = (value: boolean) => {
    console.log(value);
    try {
      setAnalysysMode(value);
      localStorage.setItem('analysys_mode', String(value));
    } catch {
      console.error('Не удалось установить глубокий анализ документов');
    }
  };

  const onPromptChoose = useCallback((curr: string) => {
    if (inputBoxRef.current) inputBoxRef.current.addText(curr);
  }, []);

  const handleClearSuccess = useCallback(() => {
    setChat([]);
    setMessageText('');
    try {
      localStorage.removeItem('chat_message_text');
    } catch (error) {
      console.warn('Failed to remove message text from localStorage:', error);
    }
    if (inputBoxRef.current) inputBoxRef.current.clearText();
  }, []);

  const handleStopRequest = useCallback(() => {
    if (currentTaskId && activeChatId) {
      stopChatById(activeChatId, currentTaskId)
        .then(() => {
          localStorage.removeItem('chat_task_id');
          localStorage.removeItem(`stream_chat_${currentTaskId}`);
          showSnackbar({ message: 'Запрос отменен', severity: 'info' });
          resetStreaming();
          setLoading(false);
        })
        .catch((error: unknown) => {
          const { generalMessage } = safeParseApiError(error);
          showSnackbar({ message: generalMessage || 'Ошибка отмены запроса', severity: 'error' });
        });
    }
  }, [currentTaskId, activeChatId, resetStreaming, showSnackbar]);

  const handleCreateChat = useCallback(
    async (title: string) => {
      await createChat(title || undefined);
    },
    [createChat]
  );

  const handleDeleteChat = useCallback(
    async (id: string) => {
      await deleteChat(id);
    },
    [deleteChat]
  );

  // История промптов пользователя текущего чата (от старых к свежим) для навигации ↑/↓.
  const userPromptHistory = useMemo(
    () =>
      chat
        .filter((m) => m.role === 'User' && typeof m.content === 'string' && m.content.trim())
        .map((m) => m.content),
    [chat]
  );

  const messagesForList = useMemo(() => {
    if (!streamingMessage) return chat;
    return [
      ...chat,
      {
        ...streamingMessage,
        role: (streamingMessage.role || 'AI') as 'AI' | 'User',
        content: displayedContent || streamingMessage.content,
        created_at: streamingMessage.created_at || new Date().toISOString(),
        progressLabel: progressLabel ?? undefined,
        progressTokens: progressTokens ?? undefined,
      } as Message,
    ];
  }, [chat, streamingMessage, displayedContent, progressLabel, progressTokens]);

  const filterControls = useMemo(
    (): {
      sourceFilters: typeof sourceFilters;
      toggleFilter: (key: keyof typeof sourceFilters) => void;
      toggleContextChat: () => void;
      updateContextMode: (mode: 'all' | 'limit') => void;
      updateContextLimit: (value: string) => void;
      updateSpecification: (id: string | null) => void;
      toggleSpecificationSelect: () => void;
      updateContextFiles: (selections: ContextFileSelection[]) => void;
      updateUrlSourceIds: (ids: string[]) => void;
    } => ({
      sourceFilters,
      toggleFilter,
      toggleContextChat,
      updateContextMode,
      updateContextLimit,
      updateSpecification,
      toggleSpecificationSelect,
      updateContextFiles,
      updateUrlSourceIds,
    }),
    [
      sourceFilters,
      toggleFilter,
      toggleContextChat,
      updateContextMode,
      updateContextLimit,
      updateSpecification,
      toggleSpecificationSelect,
      updateContextFiles,
      updateUrlSourceIds,
    ]
  );

  const generalInfoControls = useMemo(
    () => ({ expanded: expandedGeneralInfo, setExpanded: setExpandedGeneralInfo }),
    [expandedGeneralInfo]
  );

  // После re-upload: заменяем старые id на новые в выборе пула, сбрасываем выбор разделов
  // (превью нового документа может отличаться), обновляем листинги.
  const handleReuploaded = useCallback(
    (replacements: ReuploadReplacement[]) => {
      const byOldId = new Map(
        replacements.filter((r) => r.oldId).map((r) => [r.oldId as string, r.newId])
      );
      const current = sourceFilters.use_context_files || [];
      const next = current.map((s) =>
        byOldId.has(s.id) ? { id: byOldId.get(s.id) as string } : s
      );
      updateContextFiles(next);
      queryClient.invalidateQueries({ queryKey: ['contextDocuments'] });
    },
    [sourceFilters.use_context_files, updateContextFiles, queryClient]
  );

  const handleMermaidActivate = useCallback(() => {
    setMermaidActivated((prev) => !prev);
  }, []);

  const handleSavePrompt = useCallback((content: string) => onSavePrompt(content), [onSavePrompt]);
  const handleClearChat = useCallback(() => setClearConfirmOpen(true), []);
  const handleMessageTextChange = useCallback((text: string) => setMessageText(text), []);

  const { theme: chatTheme } = useChatTheme();

  if (!currProject) return <div>Выберите проект</div>;

  return (
    <ThemeProvider theme={chatTheme}>
      <CssBaseline enableColorScheme />
      {isLoadingChats ? (
        <Loader />
      ) : (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            flex: 1,
            minHeight: 0,
            gap: 0,
            padding: 0,
            backgroundColor: 'background.default',
            alignItems: 'stretch',
          }}
        >
          <ChatInfoPanel
            currProject={currProject}
            filterControls={filterControls}
            generalInfoControls={generalInfoControls}
            usageData={usageData}
            reuploadFileNames={reuploadFileNames}
            onReuploadClick={() => setReuploadDialogNames(reuploadFileNames)}
          />
          <Paper
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 0,
              backgroundColor: 'background.paper',
              boxShadow: 'none',
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderRight: `1px solid ${theme.palette.divider}`,
            })}
          >
            <ChatSwitcher
              chats={chatListItems}
              activeChatId={activeChatId}
              isLoadingChats={isLoadingChats}
              onSwitchChat={switchChat}
              onCreateChat={handleCreateChat}
              onDeleteChat={handleDeleteChat}
              setAnalysysMode={handleChangeAnalysysMode}
              analysysMode={analysysMode}
            />
            <Box
              ref={messagesContainerRef}
              flexGrow={1}
              sx={{
                overflowY: 'auto',
                px: { xs: 3, md: 5 },
                py: 5,
                backgroundColor: 'background.paper',
              }}
            >
              {messagesLoading ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Loader />
                </Box>
              ) : messagesForList.length === 0 ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 3,
                    px: 3,
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      position: 'relative',
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `radial-gradient(circle, ${theme.palette.primary.main}1F 0%, transparent 70%)`,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 8,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        opacity: 0.95,
                        animation: 'chat-hero-pulse 2.6s ease-in-out infinite',
                      },
                      '& svg': {
                        position: 'relative',
                        zIndex: 1,
                        color: '#ffffff',
                      },
                      '@keyframes chat-hero-pulse': {
                        '0%, 100%': { transform: 'scale(1)', opacity: 0.95 },
                        '50%': { transform: 'scale(1.04)', opacity: 1 },
                      },
                      '@media (prefers-reduced-motion: reduce)': {
                        '&::before': { animation: 'none' },
                      },
                    })}
                  >
                    <Sparkles size={26} strokeWidth={2} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 28, fontWeight: 600, lineHeight: 1.25, mb: 1 }}>
                      Чем помочь?
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 14,
                        color: theme.palette.text.secondary,
                        maxWidth: 480,
                      })}
                    >
                      Задайте вопрос или выберите шаблон ниже, чтобы начать.
                    </Typography>
                  </Box>
                  {favoriteTemplates.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: 'center',
                        maxWidth: 560,
                      }}
                    >
                      {favoriteTemplates.slice(0, 4).map((t) => (
                        <Chip
                          key={t.id}
                          label={t.title}
                          onClick={() => onPromptChoose(t.content)}
                          variant="outlined"
                          sx={(theme) => ({
                            cursor: 'pointer',
                            fontSize: 13,
                            height: 32,
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.secondary,
                            transition: theme.transitions.create([
                              'background-color',
                              'color',
                              'border-color',
                            ]),
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.text.primary,
                            },
                          })}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ) : (
                <MessageList
                  messages={messagesForList}
                  onSavePrompt={handleSavePrompt}
                  containerRef={messagesContainerRef}
                  projectId={currProject}
                  chatId={activeChatId}
                />
              )}
            </Box>
            <Box
              sx={{
                px: { xs: 3, md: 5 },
                pb: 4,
                pt: 1,
                backgroundColor: 'background.paper',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <InputBox
                  ref={inputBoxRef}
                  onSendMessage={handleSendMessage}
                  loading={!!streamingMessage || loading}
                  onStopRequest={handleStopRequest}
                  hasActiveRequest={!!currentTaskId && (!!streamingMessage || loading)}
                  onClearChat={activeChatId ? handleClearChat : undefined}
                  onMessageTextChange={handleMessageTextChange}
                  initialValue={messageText}
                  attachmentDrafts={attachments.drafts}
                  onAttachFile={attachments.attachFile}
                  onRemoveAttachment={attachments.removeDraft}
                  onChangeAttachmentHeaders={attachments.setDraftHeaders}
                  hasPendingAttachments={attachments.hasPending()}
                  hasReadyAttachments={attachments.getReadyIds().length > 0}
                  messageHistory={userPromptHistory}
                  mermaidActive={mermaidActivated}
                  onMermaidActivate={handleMermaidActivate}
                />
              </Box>
            </Box>
          </Paper>
          <Sidebar
            onPromptChoose={onPromptChoose}
            prompts={prompts}
            onDelete={onDeletePrompt}
            loading={promptsLoading}
            cases={casesData?.items ?? []}
            casesLoading={casesLoading}
            favoriteTemplates={favoriteTemplates}
            favoritesLoading={favoritesLoading}
          />
        </Box>
      )}
      <ClearChatDialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        chatId={activeChatId}
        showSnackbar={showSnackbar}
        onClearSuccess={handleClearSuccess}
      />
      <ReuploadFilesDialog
        open={!!reuploadDialogNames}
        fileNames={reuploadDialogNames ?? []}
        projectId={currProject}
        targetType="project"
        targetId={currProject}
        onClose={() => setReuploadDialogNames(null)}
        onReuploaded={handleReuploaded}
      />
    </ThemeProvider>
  );
}
